import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

import {
  ImageIcon,
  XIcon,
  MapPinIcon,
  CalendarIcon,
  UsersIcon,
  InfinityIcon,
  ChevronLeftIcon,
} from "lucide-react";
import ATMTextField from "../../../Atom/ATMTextField/ATMTextField";
import { useUploadImageMutation } from "../../../../redux/service/vendorService";
import { toast } from "sonner";
import { FaLocationArrow } from "react-icons/fa";
import { useCreateActivityMutation } from "../../../../redux/service/customerServices";
import ATMLoadingButton from "../../../Atom/ATMLoadingButton/ATMLoadingButton";
import moment from "moment/moment";
const categories = [
  "Tech and Gaming",
  "Volunteer Opportunities",
  "Cultural Exchanges",
  "Intellectual Pursuits",
  "Sports and Recreation",
  "Arts and Crafts",
  "Social Gatherings",
  "Educational Workshops",
  "Music and Entertainment",
  "Others",
];

const validationSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  description: Yup.string().required("Description is required"),
  category: Yup.string().required("Category is required"),
  maxParticipants: Yup.string().when("allowParticipation", {
    is: true,
    then: (schema) => schema.required("Maximum participants is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  endDateTime: Yup.string().when("isInfinite", {
    is: false,
    then: (schema) => schema.required("End date and time is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const initialValues = {
  title: "",
  description: "",
  category: "",
  allowParticipation: true,
  maxParticipants: "",
  endDateTime: "",
  isInfinite: false,
};

const mockActivityData = {
  title: "Morning Yoga Session",
  description: "Join us for an invigorating morning yoga session",
  category: "Sports & Fitness",
  location: "Wellness Hub, Delhi",
  useCurrentLocation: false,
  images: [
    {
      thumbnail:
        "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?ixlib=rb-4.0.3",
      compressed:
        "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?ixlib=rb-4.0.3",
    },
  ],
  allowParticipation: true,
  maxParticipants: "15",
  endDateTime: "2024-02-01T08:00",
  isInfinite: false,
};

export const ActivityAddForm = () => {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const [images, setImages] = useState([]);
  const navigate = useNavigate();
  const [uploadDealImage] = useUploadImageMutation();

  const handleImageUpload = async (files) => {
    const newPreviews = Array.from(files).map((file) => ({
      thumbnail: URL.createObjectURL(file),
      compressed: URL.createObjectURL(file),
      file, // Keep the file reference for upload
    }));

    // Set previews immediately
    setImages([...images, ...newPreviews]);

    // Upload each file and update Formik values
    for (const preview of newPreviews) {
      const formData = new FormData();
      formData.append("images", preview?.file);
      formData.append("model_name", "CreateDeal");
      try {
        const response = await uploadDealImage(formData).unwrap();

        // Check for successful response based on your actual API structure
        if (
          response.data &&
          response.data[0]?.thumbnail &&
          response.data[0]?.compressed
        ) {
          // Update the preview with actual URLs
          setImages((prev) =>
            prev.map((img) =>
              img.file === preview.file
                ? {
                    thumbnail: response.data[0].thumbnail,
                    compressed: response.data[0].compressed,
                    file: null, // Clear file reference after upload
                  }
                : img
            )
          );

          toast.success(response.message || "Image uploaded successfully!");
        } else {
          throw new Error("Invalid response format");
        }
      } catch (error) {
        toast.error(error.message || "Image upload failed");
        setImages((prev) => prev.filter((img) => img.file !== preview.file));
      }
    }
  };

  const removeImage = (index) => {
    if (images[index].file) {
      URL.revokeObjectURL(images[index].thumbnail);
      URL.revokeObjectURL(images[index].compressed);
    }
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
  };

  const [location, setLocation] = useState("");
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);
  const [latitudeValue, setLatitudeValue] = useState(null);
  const [longitudeValue, setLongitudeValue] = useState(null);
  const [error, setError] = useState("");
  const locationInputRef = useRef(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const apiKey = "AIzaSyCRQXge0eI79YVPiihiUrqOKv4NapMnA9I";

  const [addActivityDetail, addActivityDetailInfo] =
    useCreateActivityMutation();

  // Load Google Maps API and initialize autocomplete
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.onload = initAutocomplete;
      document.head.appendChild(script);
    } else {
      initAutocomplete();
    }

    return () => {
      if (autocomplete && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, []);

  const initAutocomplete = () => {
    if (window.google && locationInputRef.current && window.google.maps.places) {
      const newAutocomplete = new window.google.maps.places.Autocomplete(
        locationInputRef.current,
        { types: ["geocode"] }
      );
      newAutocomplete.addListener("place_changed", () => {
        const place = newAutocomplete.getPlace();
        console.log(place.geometry.location.lat());
        if (place.formatted_address) {
          console.log(place.geometry.location.lat());
          setLatitudeValue(place.geometry.location.lat());
          setLongitudeValue(place.geometry.location.lng());
          setLocation(place.formatted_address);
          setUseCurrentLocation(false);
        }
      });

      setAutocomplete(newAutocomplete);
      console.log(newAutocomplete);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation not supported by your browser");
      return;
    }

    toast.info("Fetching your location...");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        console.log(position);
        try {
          const geocoder = new window.google.maps.Geocoder();
          const { results } = await geocoder.geocode({
            location: {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            },
          });

          if (results[0]) {
            setLatitudeValue(results[0].geometry.location.lat());
            setLongitudeValue(results[0].geometry.location.lng());
            setLocation(results[0].formatted_address);
            toast.success("Location found!");
          } else {
            setError("Could not find address for this location");
          }
        } catch (err) {
          setError("Failed to get address");
          console.error(err);
        }
      },
      (error) => {
        setError("Location access was denied");
      }
    );
  };

  const handleLocationToggle = async () => {
    const newValue = !useCurrentLocation;
    setUseCurrentLocation(newValue);

    if (newValue) {
      getCurrentLocation();
    } else {
      setLocation("");
    }
  };

  const handleSubmit = async (values) => {
   const dateTime =values?.endDateTime ?  moment(values?.endDateTime) :moment(new Date());
  const date  = dateTime ?   dateTime.format("YYYY-MM-DD") : ""; 
  const time = dateTime ? dateTime.format("HH:mm") : "";  
    const formData = {
      activity_title: values?.title,
      activity_description: values?.description,
      category: values?.category,
      user_participation: values?.allowParticipation,
      maximum_participants: values?.maxParticipants,
      end_date: date || "" ,
      end_time: time || "",
      infinite_time: values?.isInfinite,
      uploaded_images: images?.map((el) => {
        return {
          thumbnail: el?.thumbnail,
          compressed: el?.compressed,
        };
      }),
      location: location,
      longitude: longitudeValue,
      latitude: latitudeValue,
    };

    try {
      await addActivityDetail(formData).unwrap();
      toast.success("Activity added successfully");
      navigate("/LocalActivities");
    } catch {
      toast.error("Failed to add Activity.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="border-b border-gray-200">
            <div className="px-8 py-6">
              <button
              type="button"
                onClick={() => navigate("/activities")}
                className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
              >
                <ChevronLeftIcon className="h-5 w-5 mr-1" />
                Back to Activities
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                {editId ? "Edit Activity" : "Create New Activity"}
              </h1>
              <p className="text-gray-600 mt-1">
                {editId
                  ? "Update your activity details"
                  : "Share an activity with your community"}
              </p>
            </div>
          </div>

          <Formik
            initialValues={editId ? mockActivityData : initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, errors, touched }) => (
              <Form className="px-8 py-6">
                <div className="space-y-8">
                  {/* Basic Information */}
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Basic Information
                    </h2>
                    <div className="space-y-4">
                      <ATMTextField
                        name="title"
                        label="Activity Title"
                        required
                        placeholder="Give your activity a clear, descriptive title"
                      />
                      <ATMTextField
                        name="description"
                        label="Description"
                        as="textarea"
                        rows={4}
                        required
                        placeholder="Describe what your activity is about"
                      />
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Category
                        </label>
                        <Field
                          name="category"
                          as="select"
                          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        >
                          <option value="">Select a category</option>
                          {categories.map((category) => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="category"
                          component="p"
                          className="mt-2 text-sm text-red-600"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Location */}
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Location
                    </h2>
                    <div className="space-y-3">
                      <div className="relative">
                        <input
                          ref={locationInputRef}
                          value={location}
                          onChange={(e) => {
                            setLocation(e.target.value);
                            setUseCurrentLocation(false);
                          }}
                          className="w-full p-2 border rounded pl-10"
                          placeholder="Search for a location"
                          disabled={useCurrentLocation}
                        />
                        <FaLocationArrow className="absolute left-3 top-3 text-gray-400" />
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="useCurrentLocation"
                          checked={useCurrentLocation}
                          onChange={handleLocationToggle}
                          className="mr-2"
                        />
                        <label
                          htmlFor="useCurrentLocation"
                          className="flex items-center"
                        >
                          <span className="ml-1">Use my current location</span>
                        </label>
                      </div>

                      {error && <p className="text-red-500 text-sm">{error}</p>}
                    </div>
                  </div>

                  {/* Images */}
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Activity Images
                    </h2>
                    <div className="bg-gray-50 rounded-xl p-6">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {images.map((image, index) => (
                          <div
                            key={index}
                            className="relative aspect-square rounded-lg overflow-hidden bg-white shadow-sm"
                          >
                            <img
                              src={image.compressed}
                              alt={`Deal image ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                removeImage(index, values, setFieldValue)
                              }
                              className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
                            >
                              <XIcon className="h-4 w-4 text-gray-600" />
                            </button>
                          </div>
                        ))}
                        {images?.length < 5 && (
                          <label className="relative aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-orange-500 cursor-pointer flex items-center justify-center bg-white transition-colors">
                            <div className="text-center p-4">
                              <ImageIcon className="mx-auto h-10 w-10 text-gray-400 mb-3" />
                              <span className="block text-sm font-medium text-gray-600">
                                Add Image
                              </span>
                              <span className="block text-xs text-gray-500 mt-1">
                                Up to 5 images
                              </span>
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) =>
                                e.target.files &&
                                handleImageUpload(
                                  e.target.files,
                                  values,
                                  setFieldValue
                                )
                              }
                            />
                          </label>
                        )}
                      </div>
                      {images.length === 0 && (
                        <p className="mt-3 text-sm text-red-600">
                          At least one image is required
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Participation */}
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Participation
                    </h2>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <h3 className="text-sm font-medium text-gray-900">
                            Allow Participation
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            Let others join your activity
                          </p>
                        </div>
                        <button
                          type="button"
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            values.allowParticipation
                              ? "bg-orange-500"
                              : "bg-gray-200"
                          }`}
                          onClick={() =>
                            setFieldValue(
                              "allowParticipation",
                              !values.allowParticipation
                            )
                          }
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              values.allowParticipation
                                ? "translate-x-5"
                                : "translate-x-0"
                            }`}
                          />
                        </button>
                      </div>
                      {values.allowParticipation && (
                        <div className="relative">
                          <UsersIcon className="absolute left-3 top-9 h-5 w-5 text-gray-400  z-50" />
                          <ATMTextField
                            name="maxParticipants"
                            label="Maximum Participants"
                            type="number"
                            min="1"
                            extraClassName="pl-10"
                            placeholder="Enter maximum number of participants"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Timing */}
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Activity Timing
                    </h2>
                    <div className="space-y-4">
                      <div className="flex items-center mb-4">
                        <Field
                          name="isInfinite"
                          type="checkbox"
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                        <div className="ml-3">
                          <label className="flex items-center text-sm font-medium text-gray-700">
                            <InfinityIcon className="h-5 w-5 mr-2" />
                            No End Date
                          </label>
                          <p className="text-sm text-gray-500">
                            Activity will continue indefinitely
                          </p>
                        </div>
                      </div>
                      {!values.isInfinite && (
                        <div className="relative">
                          <CalendarIcon className="absolute left-3 top-9 z-50 h-5 w-5 text-gray-400" />
                          <ATMTextField
                            name="endDateTime"
                            label="End Date & Time"
                            type="datetime-local"
                            min={new Date().toISOString().slice(0, 16)}
                            extraClassName="pl-10"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="mt-8 border-t pt-6">
                  {/* <button
                    type="submit"
                    className="w-full md:w-auto px-8 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium transition-colors"
                  >
                    {editId ? "Update Activity" : "Create Activity"}
                  </button> */}
                  <ATMLoadingButton 
                   loading={addActivityDetailInfo?.isLoading}
                   label='Create Activity'
                  type='submit'
                  />
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};
